import React, { useEffect, useMemo, useRef } from "react";
import { Chart, registerables } from "chart.js";
import { useSimulator } from "../Simulator/SimulatorStore";

type Series = { label: string; values: number[]; color: string };

const SERIES_COLORS: Record<string, string> = {
  "Staff Level": "#3B82F6",
  Experience: "#10B981",
  Automation: "#F59E0B",
  Maturity: "#8B5CF6",
};

Chart.register(...registerables);

export default function ProcessCapabilityMatrix({ series }: { series?: Series[] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const { processes } = useSimulator();

  const categories = useMemo(() => processes.map((p) => p.base.name), [processes]);

  const dataSeries: Series[] = useMemo(() => {
    if (series && series.length > 0) return series;
    const staffMax = 5;
    return [
      { label: "Staff Level", color: SERIES_COLORS["Staff Level"], values: processes.map((p) => (p.factors.staff / staffMax) * 100) },
      // Normalize Experience so 20% becomes 0 on the radar, 100% maps to 100.
      { label: "Experience", color: SERIES_COLORS.Experience, values: processes.map((p) => ((Math.max(20, p.factors.experience) - 20) / 80) * 100) },
      { label: "Automation", color: SERIES_COLORS.Automation, values: processes.map((p) => p.factors.automation) },
      { label: "Maturity", color: SERIES_COLORS.Maturity, values: processes.map((p) => p.factors.maturity) },
    ];
  }, [processes, series]);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;

    const datasets = dataSeries.map((s) => ({
      label: s.label,
      data: s.values,
      borderColor: s.color,
      backgroundColor: s.color + "55", 
      borderWidth: 2,
      pointBackgroundColor: "transparent",
      pointBorderColor: "transparent",
      pointBorderWidth: 0,
      pointRadius: 0,
      pointHoverRadius: 0,
      pointHitRadius: 6,
      fill: true,
    }));

    if (!chartRef.current) {
      chartRef.current = new Chart(ctx, {
        type: "radar",
        data: { labels: categories, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              callbacks: {
                title: (items) => (items && items.length ? (items[0].label as string) : ""),
                label: (ctx) => {
                  const rawLabel = ctx.dataset.label;
                  if (rawLabel === "Experience") {
                    // Reverse normalization: r (0..100) -> original 20..100
                    const orig = Math.round((ctx.parsed.r / 100) * 80 + 20);
                    return `${rawLabel}: ${orig}% (norm ${Math.round(ctx.parsed.r)}%)`;
                  }
                  return `${rawLabel}: ${Math.round(ctx.parsed.r)}%`;
                },
                labelColor: (ctx) => {
                  const color = SERIES_COLORS[ctx.dataset.label as keyof typeof SERIES_COLORS] || "#9ca3af";
                  return {
                    backgroundColor: color,
                    borderColor: color,
                    borderWidth: 1,
                    borderRadius: 0,
                  };
                },
              },
            },
          },
          interaction: { mode: "index", intersect: false },
          scales: {
            r: {
              suggestedMin: 0,
              suggestedMax: 100,
              angleLines: { display: true, color: "rgba(0,0,0,0.1)" },
              grid: { color: "#e5e7eb" },
              pointLabels: { font: { size: 12 }, color: "#374151" },
              ticks: { stepSize: 25, backdropColor: "transparent", color: "#6b7280", callback: (val: any) => `${val}` },
            },
          },
        },
      });
    } else {
      const chartInstance = chartRef.current;
      chartInstance.data.labels = categories as any;
      datasets.forEach((incoming, idx) => {
        if (!chartInstance.data.datasets[idx]) {
          chartInstance.data.datasets[idx] = incoming as any;
        } else {
          const target: any = chartInstance.data.datasets[idx];
          target.label = incoming.label;
          target.data = incoming.data as any;
          target.borderColor = incoming.borderColor;
          target.backgroundColor = incoming.backgroundColor;
          target.borderWidth = incoming.borderWidth;
          target.pointBackgroundColor = incoming.pointBackgroundColor;
          target.pointBorderColor = incoming.pointBorderColor;
          target.pointBorderWidth = incoming.pointBorderWidth;
          target.pointRadius = incoming.pointRadius;
          target.pointHoverRadius = incoming.pointHoverRadius;
          target.fill = incoming.fill;
        }
      });
      chartInstance.update("none");
    }

  }, [JSON.stringify(dataSeries), categories.join("|")]);

  useEffect(() => {
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, []);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-2">Process Capability Matrix</h4>
      <div className="relative" style={{ height: 300 }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
}
